/**
 * Utilitaires pour le module Clients Admin
 * customerOrgs = source de vérité. Période pour filtre inactifs / statut.
 */

import { FirebaseOrder, Product } from '@/models/types';
import type { CustomerOrgView } from '@/services/firebase/customer-orgs-repository';
import {
    AggregatedCustomer,
    CustomerPeriodStatus,
    CustomerFilters,
    CustomerSort,
    CustomerStats,
    CustomerTimeSeriesData,
    ProductPreference,
    AnalysisPeriod,
    CUSTOMER_STATUS_CONFIG,
} from './types';

// ============================================
// CALCUL DU STATUT CLIENT (basé sur période)
// ============================================

/**
 * Calcule le statut d'un client en fonction de la période
 * 
 * - new: Première commande dans la période
 * - existing: Première commande avant période + commande(s) dans période
 * - inactive: Aucune commande dans la période
 */
export function calculatePeriodStatus(
    firstOrderDate: Date,
    lastOrderDate: Date,
    hasOrderInPeriod: boolean,
    periodStartDate: Date
): CustomerPeriodStatus {
    // Aucune commande dans la période = inactif
    if (!hasOrderInPeriod) {
        return 'inactive';
    }

    // Première commande dans la période = nouveau
    if (firstOrderDate >= periodStartDate) {
        return 'new';
    }

    // Première commande avant la période + commande dans période = existant actif
    return 'existing';
}

/**
 * Calcule la date de début de période
 */
export function getPeriodStartDate(period: AnalysisPeriod): Date {
    const now = new Date();
    const startDate = new Date(now);
    startDate.setDate(startDate.getDate() - period);
    startDate.setHours(0, 0, 0, 0);
    return startDate;
}

/**
 * Vérifie si une date est dans la période
 */
export function isDateInPeriod(date: Date, periodStartDate: Date): boolean {
    return date >= periodStartDate;
}

/**
 * Dérive le statut période (new / existing / inactive) depuis un CustomerOrgView.
 * firstPaidAt / lastPaidAt + periodStart.
 */
export function derivePeriodStatusFromCustomerOrg(
    c: CustomerOrgView,
    periodStartDate: Date
): CustomerPeriodStatus {
    const last = c.lastPaidAt;
    const first = c.firstPaidAt;
    if (!last) return 'inactive';
    const hasOrderInPeriod = last >= periodStartDate;
    if (!hasOrderInPeriod) return 'inactive';
    if (!first) return 'existing';
    if (first >= periodStartDate) return 'new';
    return 'existing';
}

/**
 * Filtre client-side par recherche (nom, email, TikTok, tél) sur la page courante.
 */
export function filterCustomerOrgsBySearch(
    items: CustomerOrgView[],
    search: string
): CustomerOrgView[] {
    if (!search.trim()) return items;
    const s = search.toLowerCase().trim();
    return items.filter((c) => {
        const fn = (c.fullName ?? '').toLowerCase();
        const em = (c.email ?? '').toLowerCase();
        const tik = (c.tiktokUsername ?? '').toLowerCase();
        const ph = (c.phone ?? '');
        return fn.includes(s) || em.includes(s) || tik.includes(s) || ph.includes(s);
    });
}

// ============================================
// AGRÉGATION DES DONNÉES CLIENTS (legacy, orders)
// ============================================

/**
 * Agrège les commandes en profils clients avec stats période
 */
export function aggregateCustomersFromOrders(
    orders: FirebaseOrder[],
    period: AnalysisPeriod = 30
): AggregatedCustomer[] {
    const periodStartDate = getPeriodStartDate(period);
    const now = new Date();

    // Grouper par email (clé unique client)
    const customerMap = new Map<string, {
        email: string;
        firstName?: string;
        lastName?: string;
        tiktokUsername?: string;
        phone?: string;
        orders: FirebaseOrder[];
        periodOrders: FirebaseOrder[];
    }>();

    orders.forEach(order => {
        const email = order.orderInfo.email.toLowerCase().trim();
        if (!email) return;

        // Récupérer la date de la commande
        let orderDate: Date;
        if (order.createdAt && 'toDate' in order.createdAt) {
            orderDate = order.createdAt.toDate();
        } else if (order.createdAt instanceof Date) {
            orderDate = order.createdAt;
        } else {
            orderDate = new Date(order.createdAt as any);
        }

        const isInPeriod = isDateInPeriod(orderDate, periodStartDate);

        if (!customerMap.has(email)) {
            customerMap.set(email, {
                email,
                firstName: order.orderInfo.firstName,
                lastName: order.orderInfo.lastName,
                tiktokUsername: order.products?.[0]?.tiktokUsername,
                phone: order.orderInfo.phone,
                orders: [],
                periodOrders: [],
            });
        }

        const customer = customerMap.get(email)!;
        customer.orders.push(order);
        if (isInPeriod) {
            customer.periodOrders.push(order);
        }

        // Mettre à jour les infos si plus récentes
        if (!customer.tiktokUsername && order.products?.[0]?.tiktokUsername) {
            customer.tiktokUsername = order.products[0].tiktokUsername;
        }
        if (!customer.phone && order.orderInfo.phone) {
            customer.phone = order.orderInfo.phone;
        }
    });

    // Transformer en AggregatedCustomer
    const customers: AggregatedCustomer[] = [];

    customerMap.forEach((data, email) => {
        const { orders: allOrders, periodOrders } = data;

        // Calculer les dates
        const orderDates = allOrders.map(o => {
            if (o.createdAt && 'toDate' in o.createdAt) {
                return o.createdAt.toDate();
            } else if (o.createdAt instanceof Date) {
                return o.createdAt;
            }
            return new Date(o.createdAt as any);
        }).sort((a, b) => a.getTime() - b.getTime());

        const firstOrderDate = orderDates[0];
        const lastOrderDate = orderDates[orderDates.length - 1];

        // Calculer le CA total
        const totalRevenue = allOrders.reduce((sum, order) => {
            const productRevenue = order.products?.reduce((pSum, p) => {
                if (p.paymentInfo.status === 'paid') {
                    return pSum + parseFloat(p.amount || '0');
                }
                return pSum;
            }, 0) || 0;

            // Ajouter frais de livraison si payés
            const shippingRevenue = order.deliveryInfo?.paymentStatus === 'paid'
                ? (order.deliveryInfo.shippingCost || 0)
                : 0;

            return sum + productRevenue + shippingRevenue;
        }, 0);

        // Calculer le CA période
        const periodRevenue = periodOrders.reduce((sum, order) => {
            const productRevenue = order.products?.reduce((pSum, p) => {
                if (p.paymentInfo.status === 'paid') {
                    return pSum + parseFloat(p.amount || '0');
                }
                return pSum;
            }, 0) || 0;

            const shippingRevenue = order.deliveryInfo?.paymentStatus === 'paid'
                ? (order.deliveryInfo.shippingCost || 0)
                : 0;

            return sum + productRevenue + shippingRevenue;
        }, 0);

        // Calculer la fréquence d'achat (médiane des intervalles)
        let purchaseFrequencyDays: number | undefined;
        if (orderDates.length >= 2) {
            const intervals: number[] = [];
            for (let i = 1; i < orderDates.length; i++) {
                const diffMs = orderDates[i].getTime() - orderDates[i - 1].getTime();
                const diffDays = Math.round(diffMs / (1000 * 60 * 60 * 24));
                if (diffDays > 0) intervals.push(diffDays);
            }
            if (intervals.length > 0) {
                intervals.sort((a, b) => a - b);
                const mid = Math.floor(intervals.length / 2);
                purchaseFrequencyDays = intervals.length % 2 === 0
                    ? Math.round((intervals[mid - 1] + intervals[mid]) / 2)
                    : intervals[mid];
            }
        }

        // Jours depuis dernière commande
        const daysSinceLastOrder = Math.floor(
            (now.getTime() - lastOrderDate.getTime()) / (1000 * 60 * 60 * 24)
        );

        // Calculer le statut période
        const hasOrderInPeriod = periodOrders.length > 0;
        const periodStatus = calculatePeriodStatus(
            firstOrderDate,
            lastOrderDate,
            hasOrderInPeriod,
            periodStartDate
        );

        // Extraire les produits préférés
        const preferredProducts = extractPreferredProducts(allOrders);

        customers.push({
            id: email,
            email,
            firstName: data.firstName,
            lastName: data.lastName,
            fullName: [data.firstName, data.lastName].filter(Boolean).join(' ') || email.split('@')[0],
            tiktokUsername: data.tiktokUsername,
            phone: data.phone,

            // Stats totales
            totalOrders: allOrders.length,
            totalRevenue,
            averageCart: allOrders.length > 0 ? totalRevenue / allOrders.length : 0,
            firstOrderDate,
            lastOrderDate,

            // Stats période
            periodOrders: periodOrders.length,
            periodRevenue,
            periodAverageCart: periodOrders.length > 0 ? periodRevenue / periodOrders.length : 0,

            // Comportement
            periodStatus,
            purchaseFrequencyDays,
            daysSinceLastOrder,

            // Produits
            preferredProducts,
            orderIds: allOrders.map(o => o.id!).filter(Boolean),
        });
    });

    return customers;
}

/**
 * Extrait les produits préférés d'un client
 */
function extractPreferredProducts(orders: FirebaseOrder[]): ProductPreference[] {
    const productMap = new Map<string, ProductPreference>();

    orders.forEach(order => {
        order.products?.forEach((product: Product) => {
            if (product.paymentInfo.status !== 'paid') return;

            const key = product.reference.toLowerCase();
            const amount = parseFloat(product.amount || '0');

            if (!productMap.has(key)) {
                productMap.set(key, {
                    reference: product.reference,
                    model: product.model,
                    count: 0,
                    totalAmount: 0,
                });
            }

            const pref = productMap.get(key)!;
            pref.count++;
            pref.totalAmount += amount;
        });
    });

    return Array.from(productMap.values())
        .sort((a, b) => b.count - a.count)
        .slice(0, 5);
}

// ============================================
// STATISTIQUES GLOBALES
// ============================================

/**
 * Calcule les statistiques clients sur la période
 */
export function calculateCustomerStats(
    customers: AggregatedCustomer[],
    period: AnalysisPeriod
): CustomerStats {
    const newCustomers = customers.filter(c => c.periodStatus === 'new');
    const existingCustomers = customers.filter(c => c.periodStatus === 'existing');
    const inactiveCustomers = customers.filter(c => c.periodStatus === 'inactive');
    const activeCustomers = [...newCustomers, ...existingCustomers];

    const totalCustomers = customers.length;
    const totalRevenue = activeCustomers.reduce((sum, c) => sum + c.periodRevenue, 0);
    const totalOrders = activeCustomers.reduce((sum, c) => sum + c.periodOrders, 0);

    const newCustomersRevenue = newCustomers.reduce((sum, c) => sum + c.periodRevenue, 0);
    const existingCustomersRevenue = existingCustomers.reduce((sum, c) => sum + c.periodRevenue, 0);

    const newCustomersOrders = newCustomers.reduce((sum, c) => sum + c.periodOrders, 0);
    const existingCustomersOrders = existingCustomers.reduce((sum, c) => sum + c.periodOrders, 0);

    // Calculer la fréquence moyenne (parmi les clients avec données)
    const customersWithFrequency = activeCustomers.filter(c => c.purchaseFrequencyDays);
    const averagePurchaseFrequency = customersWithFrequency.length > 0
        ? customersWithFrequency.reduce((sum, c) => sum + (c.purchaseFrequencyDays || 0), 0) / customersWithFrequency.length
        : 0;

    return {
        totalCustomers,
        newCustomers: newCustomers.length,
        existingCustomers: existingCustomers.length,
        inactiveCustomers: inactiveCustomers.length,

        newCustomersPercent: totalCustomers > 0 ? (newCustomers.length / totalCustomers) * 100 : 0,
        existingCustomersPercent: totalCustomers > 0 ? (existingCustomers.length / totalCustomers) * 100 : 0,
        inactiveCustomersPercent: totalCustomers > 0 ? (inactiveCustomers.length / totalCustomers) * 100 : 0,

        totalRevenue,
        newCustomersRevenue,
        existingCustomersRevenue,

        newRevenuePercent: totalRevenue > 0 ? (newCustomersRevenue / totalRevenue) * 100 : 0,
        existingRevenuePercent: totalRevenue > 0 ? (existingCustomersRevenue / totalRevenue) * 100 : 0,

        totalOrders,
        newCustomersOrders,
        existingCustomersOrders,

        averageCart: totalOrders > 0 ? totalRevenue / totalOrders : 0,
        averagePurchaseFrequency: Math.round(averagePurchaseFrequency),
    };
}

/**
 * Génère les données de séries temporelles pour les graphiques
 */
export function generateTimeSeriesData(
    orders: FirebaseOrder[],
    period: AnalysisPeriod
): CustomerTimeSeriesData[] {
    const periodStartDate = getPeriodStartDate(period);
    const now = new Date();

    // Créer un map des dates
    const dataMap = new Map<string, CustomerTimeSeriesData>();

    // Initialiser toutes les dates de la période
    const currentDate = new Date(periodStartDate);
    while (currentDate <= now) {
        const dateKey = currentDate.toISOString().split('T')[0];
        dataMap.set(dateKey, {
            date: dateKey,
            newCustomers: 0,
            existingCustomers: 0,
            newRevenue: 0,
            existingRevenue: 0,
            newOrders: 0,
            existingOrders: 0,
        });
        currentDate.setDate(currentDate.getDate() + 1);
    }

    // Tracker les clients vus (pour déterminer nouveau vs existant)
    const clientFirstOrderDate = new Map<string, Date>();

    // Premier pass: déterminer la date de première commande de chaque client
    orders.forEach(order => {
        const email = order.orderInfo.email.toLowerCase().trim();
        if (!email) return;

        let orderDate: Date;
        if (order.createdAt && 'toDate' in order.createdAt) {
            orderDate = order.createdAt.toDate();
        } else if (order.createdAt instanceof Date) {
            orderDate = order.createdAt;
        } else {
            orderDate = new Date(order.createdAt as any);
        }

        if (!clientFirstOrderDate.has(email) || orderDate < clientFirstOrderDate.get(email)!) {
            clientFirstOrderDate.set(email, orderDate);
        }
    });

    // Second pass: comptabiliser les commandes par jour
    const dailyNewClients = new Map<string, Set<string>>();
    const dailyExistingClients = new Map<string, Set<string>>();

    orders.forEach(order => {
        const email = order.orderInfo.email.toLowerCase().trim();
        if (!email) return;

        let orderDate: Date;
        if (order.createdAt && 'toDate' in order.createdAt) {
            orderDate = order.createdAt.toDate();
        } else if (order.createdAt instanceof Date) {
            orderDate = order.createdAt;
        } else {
            orderDate = new Date(order.createdAt as any);
        }

        // Ignorer les commandes hors période
        if (orderDate < periodStartDate) return;

        const dateKey = orderDate.toISOString().split('T')[0];
        const data = dataMap.get(dateKey);
        if (!data) return;

        // Calculer le revenu de la commande
        const revenue = (order.products?.reduce((sum, p) => {
            if (p.paymentInfo.status === 'paid') {
                return sum + parseFloat(p.amount || '0');
            }
            return sum;
        }, 0) || 0) + (order.deliveryInfo?.paymentStatus === 'paid' ? (order.deliveryInfo.shippingCost || 0) : 0);

        // Déterminer si nouveau ou existant
        const firstOrderDate = clientFirstOrderDate.get(email)!;
        const isNew = firstOrderDate >= periodStartDate &&
            firstOrderDate.toISOString().split('T')[0] === dateKey;

        if (isNew) {
            if (!dailyNewClients.has(dateKey)) {
                dailyNewClients.set(dateKey, new Set());
            }
            dailyNewClients.get(dateKey)!.add(email);
            data.newRevenue += revenue;
            data.newOrders++;
        } else if (firstOrderDate < periodStartDate) {
            if (!dailyExistingClients.has(dateKey)) {
                dailyExistingClients.set(dateKey, new Set());
            }
            dailyExistingClients.get(dateKey)!.add(email);
            data.existingRevenue += revenue;
            data.existingOrders++;
        }
    });

    // Mettre à jour les compteurs de clients uniques
    dailyNewClients.forEach((clients, dateKey) => {
        const data = dataMap.get(dateKey);
        if (data) {
            data.newCustomers = clients.size;
        }
    });

    dailyExistingClients.forEach((clients, dateKey) => {
        const data = dataMap.get(dateKey);
        if (data) {
            data.existingCustomers = clients.size;
        }
    });

    return Array.from(dataMap.values()).sort((a, b) => a.date.localeCompare(b.date));
}

// ============================================
// FILTRAGE ET TRI
// ============================================

/**
 * Filtre les clients selon les critères
 */
export function filterCustomers(
    customers: AggregatedCustomer[],
    filters: CustomerFilters
): AggregatedCustomer[] {
    return customers.filter(customer => {
        // Filtre par recherche
        if (filters.search) {
            const searchLower = filters.search.toLowerCase();
            const matchesSearch =
                customer.fullName.toLowerCase().includes(searchLower) ||
                customer.email.toLowerCase().includes(searchLower) ||
                (customer.tiktokUsername?.toLowerCase().includes(searchLower)) ||
                (customer.phone?.includes(searchLower));

            if (!matchesSearch) return false;
        }

        // Filtre par statut
        if (filters.statuses.length > 0) {
            if (!filters.statuses.includes(customer.periodStatus)) {
                return false;
            }
        }

        return true;
    });
}

/**
 * Trie les clients selon les critères
 */
export function sortCustomers(
    customers: AggregatedCustomer[],
    sort: CustomerSort
): AggregatedCustomer[] {
    return [...customers].sort((a, b) => {
        let comparison = 0;

        switch (sort.field) {
            case 'fullName':
                comparison = a.fullName.localeCompare(b.fullName);
                break;
            case 'periodStatus':
                const statusOrder = { new: 0, existing: 1, inactive: 2 };
                comparison = statusOrder[a.periodStatus] - statusOrder[b.periodStatus];
                break;
            case 'firstOrderDate':
                comparison = a.firstOrderDate.getTime() - b.firstOrderDate.getTime();
                break;
            case 'lastOrderDate':
                comparison = a.lastOrderDate.getTime() - b.lastOrderDate.getTime();
                break;
            case 'periodOrders':
                comparison = a.periodOrders - b.periodOrders;
                break;
            case 'periodRevenue':
                comparison = a.periodRevenue - b.periodRevenue;
                break;
            case 'averageCart':
                comparison = a.averageCart - b.averageCart;
                break;
            case 'purchaseFrequencyDays':
                const freqA = a.purchaseFrequencyDays || 9999;
                const freqB = b.purchaseFrequencyDays || 9999;
                comparison = freqA - freqB;
                break;
        }

        return sort.direction === 'desc' ? -comparison : comparison;
    });
}

// ============================================
// FORMATAGE
// ============================================

/**
 * Formate un montant en euros
 */
export function formatCurrency(amount: number): string {
    return new Intl.NumberFormat('fr-FR', {
        style: 'currency',
        currency: 'EUR',
        minimumFractionDigits: 0,
        maximumFractionDigits: 2,
    }).format(amount);
}

/**
 * Formate une date en français
 */
export function formatDate(date: Date): string {
    return new Intl.DateTimeFormat('fr-FR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
    }).format(date);
}

/**
 * Formate une date relative (il y a X jours)
 */
export function formatRelativeDate(date: Date): string {
    const now = new Date();

    // Pour comparer les jours calendaires, on normalise les dates à minuit
    const d1 = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const d2 = new Date(date.getFullYear(), date.getMonth(), date.getDate());

    const diffMs = d1.getTime() - d2.getTime();
    const diffDays = Math.round(diffMs / (1000 * 60 * 60 * 24));

    if (diffDays === 0) return "Aujourd'hui";
    if (diffDays === 1) return 'Hier';
    if (diffDays < 7) return `Il y a ${diffDays} jours`;
    if (diffDays < 30) return `Il y a ${Math.floor(diffDays / 7)} semaine${Math.floor(diffDays / 7) > 1 ? 's' : ''}`;
    if (diffDays < 365) return `Il y a ${Math.floor(diffDays / 30)} mois`;
    return `Il y a ${Math.floor(diffDays / 365)} an${Math.floor(diffDays / 365) > 1 ? 's' : ''}`;
}

/**
 * Formate la fréquence d'achat
 */
export function formatPurchaseFrequency(days: number | undefined): string {
    if (!days) return '—';
    if (days === 1) return 'Chaque jour';
    if (days < 7) return `Tous les ${days} jours`;
    if (days === 7) return 'Chaque semaine';
    if (days < 30) return `Toutes les ${Math.round(days / 7)} semaines`;
    if (days === 30) return 'Chaque mois';
    return `Tous les ${Math.round(days / 30)} mois`;
}

/**
 * Formate un pourcentage
 */
export function formatPercent(value: number): string {
    return `${Math.round(value)}%`;
}

// Export de la config pour accès externe
export { CUSTOMER_STATUS_CONFIG };
